import React from 'react'
import { OUTCOME_LINES_WIDTH } from '@/features/market/outcome-list/outcomePointer'

// Shimmer animation styles (same as MarketListTemplate loading state)
if (typeof document !== 'undefined' && !document.getElementById('shimmer-styles')) {
  const style = document.createElement('style')
  style.id = 'shimmer-styles'
  style.textContent = `
    @keyframes shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
  `
  document.head.appendChild(style)
}

const ShimmerEffect = ({ className = '', children }) => (
  <div className={`relative overflow-hidden ${className}`}>
    {children}
    <div
      className="absolute inset-0 bg-gradient-to-r from-transparent via-Neutral-3/30 to-transparent"
      style={{
        animation: 'shimmer 2s infinite',
        transform: 'translateX(-100%)',
      }}
    />
  </div>
)

const BinaryOutcomeSkeleton = () => (
  <div className="flex my-auto w-full gap-2">
    <div className="flex flex-col items-center justify-center h-12 p-4 rounded-lg bg-surface-3/30 w-full">
      <div className="animate-pulse">
        <div className="h-2 bg-surface-3 rounded w-12" />
      </div>
    </div>
    <div className="flex flex-col items-center justify-center h-12 p-4 rounded-lg bg-surface-3/50 w-full">
      <div className="animate-pulse">
        <div className="h-2 bg-surface-3 rounded w-10" />
      </div>
    </div>
  </div>
)

const MultiOutcomeSkeleton = ({ outcomeCount = 3 }) => (
  <div className="w-full space-y-3">
    {Array.from({ length: outcomeCount }).map((_, i) => (
      <div key={i} className="relative overflow-hidden rounded-lg">
        <div className="animate-pulse outcome-item relative bg-surface-2 border border-Neutral-2 rounded-lg p-3 transition-colors">
          <div
            className="absolute inset-0 bg-surface-3 rounded-lg"
            style={{ width: `${30 + i * 20}%`, opacity: 0.1 }}
          />
          <div className="flex items-center justify-between w-full relative z-10">
            <div className="flex items-center gap-3">
              <div className="h-4 bg-surface-3 rounded w-24" />
            </div>
            <div className="h-4 bg-surface-3 rounded w-8" />
          </div>
        </div>
      </div>
    ))}
  </div>
)

const QuestionHeaderSkeleton = () => (
  <div className="space-y-3">
    <div className="animate-pulse">
      <div className="space-y-2 mb-4">
        <div className="h-6 bg-surface-3 rounded w-full" />
        <div className="h-6 bg-surface-3 rounded w-3/4" />
      </div>
    </div>
  </div>
)

const QuestionFooterSkeleton = () => (
  <div>
    <div className="animate-pulse">
      <div className="flex justify-between !items-center pt-3 pb-0 border-t border-Neutral-2 !h-8">
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 bg-surface-3 rounded" />
          <div className="h-4 bg-surface-3 rounded w-20" />
        </div>
        <div className="flex items-center gap-2">
          <div className="h-4 bg-surface-3 rounded w-16" />
          <div className="w-4 h-4 bg-surface-3 rounded" />
        </div>
      </div>
    </div>
  </div>
)

/**
 * Single market card skeleton used when the list is loading.
 * Matches MarketListTemplate's onRenderSegments skeleton exactly.
 * @param {boolean} isYesNo - binary (two outcomes) vs multi-outcome
 * @param {number} outcomeCount - number of outcome rows when multi-outcome
 */
export function MarketListCardSkeletonItem({ isYesNo = false, outcomeCount = 3 }) {
  return (
    <article className="question-card h-[238px] flex flex-col">
      <ShimmerEffect className="bg-surface-2 p-4 rounded-lg border border-Neutral-3 overflow-hidden flex-1">
        <div className="animate-pulse h-full">
          <div className="flex flex-col h-full">
            <div className="flex-shrink-0 mb-3">
              <QuestionHeaderSkeleton />
            </div>
            <div className="flex-grow overflow-hidden min-h-0 flex">
              {isYesNo ? <BinaryOutcomeSkeleton /> : <MultiOutcomeSkeleton outcomeCount={outcomeCount} />}
            </div>
            <div className="flex-shrink-0">
              <QuestionFooterSkeleton />
            </div>
          </div>
        </div>
      </ShimmerEffect>
    </article>
  )
}

/** Same deterministic config as MarketListTemplate getSkeletonConfig */
function getSkeletonConfig(index) {
  const seed = index * 7 + 13
  if (seed % 3 === 0) return { isYesNo: true }
  return { isYesNo: false, outcomeCount: (seed % 4) + 2 }
}

/**
 * Card skeleton for a given grid index (varied binary/multi-outcome).
 * Use this in feed loading and anywhere the list template loading state should match.
 */
export function MarketListCardSkeleton({ index = 0 }) {
  const config = getSkeletonConfig(index)
  return <MarketListCardSkeletonItem isYesNo={config.isYesNo} outcomeCount={config.outcomeCount} />
}

/**
 * Fixture-row skeleton for the list view. Mirrors SportEventCompactRow's
 * columns (kickoff, sport over match name, volume, crested outcome lines) so the loading
 * frame has the same height and padding as the rows that replace it.
 */
export function MarketListFixtureRowSkeleton() {
  return (
    <article className="question-card">
      {/* No frame of its own: a loading row is a line inside the list's panel, which
          carries the surface, the outline and the separators. */}
      <ShimmerEffect className="bg-surface-2 overflow-hidden">
        <div className="animate-pulse flex flex-nowrap items-center gap-x-4 px-4 py-3">
          <div className="flex w-14 shrink-0 flex-col items-center gap-1.5">
            <div className="h-4 w-10 rounded bg-surface-3" />
            <div className="h-3 w-8 rounded bg-surface-3" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <div className="flex h-4 items-center gap-1.5">
              <div className="h-4 w-4 shrink-0 rounded-full bg-surface-3" />
              <div className="h-3 w-28 rounded bg-surface-3" />
            </div>
            <div className="h-3.5 w-48 rounded bg-surface-3" />
          </div>
          <div className="flex w-32 shrink-0 items-center justify-start pl-8">
            <div className="h-3 w-12 rounded bg-surface-3" />
          </div>
          <div className={`flex shrink-0 flex-col gap-0.5 ${OUTCOME_LINES_WIDTH}`}>
            {[0, 1, 2].map(i => (
              <div key={i} className="flex h-5 items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="h-5 w-5 shrink-0 rounded-full bg-surface-3" />
                  <div className={`h-3 rounded bg-surface-3 ${i === 1 ? 'w-12' : 'w-16'}`} />
                </div>
                <div className="h-3 w-8 rounded bg-surface-3" />
              </div>
            ))}
          </div>
        </div>
      </ShimmerEffect>
    </article>
  )
}

/**
 * Market-row skeleton for the list view. Mirrors MarketCompactRow's columns — the date
 * over its distance, a two-line title, the volume, two outcome lines with a figure at the
 * right edge — including the top alignment its cells sit at, so the loading frame has the
 * same height and padding as the rows that replace it. No bar for the wager toggle: it
 * renders only for a reader holding a position, which most rows are not.
 */
export function MarketListRowSkeleton() {
  return (
    <article className="question-card">
      {/* No frame of its own: a loading row is a line inside the list's panel, which
          carries the surface, the outline and the separators. */}
      <ShimmerEffect className="bg-surface-2 overflow-hidden">
        <div className="animate-pulse flex flex-nowrap items-start gap-x-4 px-4 py-3">
          <div className="flex h-12 w-14 shrink-0 flex-col items-center justify-center gap-1.5">
            <div className="h-3.5 w-10 rounded bg-surface-3" />
            <div className="h-3 w-6 rounded bg-surface-3" />
          </div>
          <div className="flex min-h-12 min-w-0 flex-1 flex-col justify-center gap-2">
            <div className="h-3.5 w-3/4 rounded bg-surface-3" />
            <div className="h-3.5 w-1/2 rounded bg-surface-3" />
          </div>
          <div className="flex h-12 w-32 shrink-0 items-center justify-start pl-8">
            <div className="h-3 w-12 rounded bg-surface-3" />
          </div>
          <div className={`flex min-h-12 shrink-0 flex-col justify-center gap-0.5 ${OUTCOME_LINES_WIDTH}`}>
            {[0, 1].map(i => (
              <div key={i} className="flex h-8 items-center justify-between gap-3">
                <div className={`h-3 rounded bg-surface-3 ${i === 0 ? 'w-24' : 'w-16'}`} />
                <div className="h-3 w-8 rounded bg-surface-3" />
              </div>
            ))}
          </div>
        </div>
      </ShimmerEffect>
    </article>
  )
}
